/**
 * Shared price→x geometry for the ladder and the evidence strip.
 *
 * Everything price-related on this page lives on ONE ruler (spec §5): the
 * ladder band, the asking-price marker and every comparable marker all map
 * through this function, so evidence and verdict can never disagree about
 * where a price sits.
 *
 * RTL (spec §4/§9): the ruler mirrors — low prices on the right, high on the
 * left — while numerals stay Western. Mirroring flips the coordinate here;
 * the SVG itself keeps LTR geometry.
 */

export type RulerDirection = 'ltr' | 'rtl'

export interface RulerGeometry {
  /** Inner drawing extent — padded so edge labels never clip. */
  x0: number
  x1: number
  min: number
  max: number
}

export function rulerGeometry(
  domain: { min: number; max: number },
  width: number,
  pad: number,
): RulerGeometry {
  return { x0: pad, x1: width - pad, min: domain.min, max: domain.max }
}

/** Fractional position of `value` on the ruler, 0..1 (unmirrored). */
export function rulerFraction(geom: RulerGeometry, value: number): number {
  const span = geom.max - geom.min
  if (!Number.isFinite(value) || span <= 0) return 0.5
  const t = (value - geom.min) / span
  return Math.min(1, Math.max(0, t))
}

/** Pixel x for `value`; mirrored in RTL so the ruler reads high→left. */
export function rulerX(geom: RulerGeometry, value: number, dir: RulerDirection): number {
  const t = rulerFraction(geom, value)
  return dir === 'rtl' ? geom.x1 - t * (geom.x1 - geom.x0) : geom.x0 + t * (geom.x1 - geom.x0)
}
